import React, { useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { copyOutline, checkmarkOutline } from 'ionicons/icons';
import './TerminalDemo.css';

const TerminalDemo = ({ projectId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [copiedCommand, setCopiedCommand] = useState(null);

  const steps = [
    {
      id: 'install',
      command: 'npm install -g @zibby/cli',
      output: [
        { text: 'added 142 packages in 8s', delay: 200 },
        { text: '✓ @zibby/cli@0.1.0 installed', delay: 400, success: true }
      ]
    },
    {
      id: 'init',
      command: `cd your-project && zibby init --agent claude --cloud-sync --headless`,
      output: [
        { text: '🔄 Setting up with provided options...', delay: 200 },
        { text: '✓ Teamy initialized!', delay: 400, success: true },
        { text: '✓ Playwright MCP configured', delay: 600, success: true }
      ]
    },
    {
      id: 'run',
      command: `zibby test test-specs/auth/login.txt --project ${projectId} --collection "My Tests"`,
      output: [
        { text: '▶ Starting browser...', delay: 200 },
        { text: '▶ Recording session...', delay: 400 },
        { text: '✓ Test execution complete', delay: 600, success: true },
        { text: '📤 Uploading to Teamy Cloud...', delay: 800 },
        { text: '📹 Video: https://zibby.app/executions/...', delay: 1000, link: true }
      ]
    }
  ];

  useEffect(() => {
    // Auto-advance through steps
    if (currentStep < steps.length) {
      const currentStepData = steps[currentStep];
      const lastOutput = currentStepData.output[currentStepData.output.length - 1];
      const totalDelay = lastOutput.delay + 300; // Wait 300ms after last output

      const timer = setTimeout(() => {
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      }, totalDelay);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleCopyCommand = async (command, commandId) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(commandId);
      setTimeout(() => setCopiedCommand(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="terminal-demo">
      <div className="terminal-window">
        {/* Terminal Header */}
        <div className="terminal-header">
          <div className="terminal-buttons">
            <span className="terminal-button terminal-close"></span>
            <span className="terminal-button terminal-minimize"></span>
            <span className="terminal-button terminal-maximize"></span>
          </div>
        </div>

        {/* Terminal Content */}
        <div className="terminal-content">
          {steps.map((step, index) => {
            const isActive = index <= currentStep;

            return (
              <div key={step.id} className="terminal-step">
                {/* Command Line */}
                <div className={`terminal-line ${isActive ? 'active' : ''}`}>
                  <span className="terminal-prompt">$</span>
                  <span className="terminal-command">
                    {step.command}
                  </span>
                  {isActive && (
                    <button
                      className="terminal-copy-btn"
                      onClick={() => handleCopyCommand(step.command, step.id)}
                      title="Copy command"
                    >
                      <IonIcon 
                        icon={copiedCommand === step.id ? checkmarkOutline : copyOutline} 
                      />
                    </button>
                  )}
                </div>

                {/* Output Lines */}
                {isActive && step.output.map((output, outputIndex) => (
                  <div
                    key={outputIndex}
                    className={`terminal-output ${output.success ? 'success' : ''} ${output.link ? 'link' : ''}`}
                    style={{
                      animationDelay: `${output.delay}ms`
                    }}
                  >
                    {output.text}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Blinking Cursor */}
          {currentStep === steps.length - 1 && (
            <div className="terminal-line">
              <span className="terminal-prompt">$</span>
              <span className="terminal-cursor">_</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerminalDemo;

